import { useMemo } from 'react'
import { Page } from 'pages/layout'
import { Box, Flex, Heading, Loading, Text, toast } from '@workwhile/ui'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useCompanyLocationTutorials,
  useInvalidateCompanyLocationTutorials,
  useRemoveLocationTutorialMutation,
} from 'queries/company'
import { MarkdownContent, TutorialCategory } from 'api/shift/typings'
import { AllLocationTutorialCategories } from 'api/company'
import { convertLegacyTutorialToMarkdown } from 'lib/tutorial'
import { TutorialCard } from 'components/Tutorials/TutorialCard'
import { useCompanyLocation } from 'hooks/useCompanyLocation'

export const LocationInstructionsPage = () => {
  const locationId = Number(useParams<{ locationId: string }>().locationId)
  const navigate = useNavigate()
  const { data, isFetching } = useCompanyLocationTutorials(locationId)
  const location = useCompanyLocation(locationId)
  const invalidateTutorials = useInvalidateCompanyLocationTutorials()
  const { mutate: removeLocationTutorial, isPending: isRemoving } =
    useRemoveLocationTutorialMutation({
      locationId,
      onSuccess() {
        invalidateTutorials(locationId)
      },
      onError(error) {
        toast.error(error.message)
      },
    })
  const activeTutorials = useMemo(() => {
    if (!data) {
      return []
    }
    return data.map((tutorial) => ({
      ...tutorial,
      content: convertLegacyTutorialToMarkdown(tutorial.content),
    }))
  }, [data])

  const inactiveTutorials = useMemo(
    () =>
      AllLocationTutorialCategories.filter(
        (category) => !activeTutorials.some((t) => t.category === category)
      ).map((category) => ({
        category: category as TutorialCategory,
        content: [] as MarkdownContent[],
      })),
    [activeTutorials]
  )

  const handleRemove = ({ tutorialId }: { tutorialId: number }) => {
    removeLocationTutorial({ tutorialId })
  }

  const handleClick = (tutorialId: number | string) => {
    navigate(`/location/${locationId}/instruction/${tutorialId}`)
  }

  if (isFetching) {
    return <Loading />
  }

  return (
    <Page
      title={`Location Instructions ${
        location?.name ? `for ${location?.name}` : ''
      }`}
    >
      <Text>
        Select any of the instruction categories below to add to your location.
      </Text>
      {activeTutorials.length > 0 ? (
        <Box mt={4}>
          <Heading level={3}>Active</Heading>
          <Flex mx={-2} flexWrap={'wrap'}>
            {activeTutorials.map((tutorial) => (
              <Box
                key={tutorial.id}
                width={[1, 1 / 2, 1 / 2, 1 / 3, 1 / 4]}
                p={2}
              >
                <TutorialCard
                  data={tutorial}
                  removeTitle="Remove Instructions for all shifts associated with this location"
                  isRemoving={isRemoving}
                  onRemove={handleRemove}
                  onClick={handleClick}
                />
              </Box>
            ))}
          </Flex>
        </Box>
      ) : null}
      {inactiveTutorials.length > 0 ? (
        <Box mt={4}>
          {activeTutorials.length > 0 ? (
            <Heading level={3}>Inactive</Heading>
          ) : null}
          <Flex mx={-2} flexWrap={'wrap'}>
            {inactiveTutorials.map((tutorial) => (
              <Box
                key={tutorial.category}
                width={[1, 1 / 2, 1 / 2, 1 / 3, 1 / 4]}
                p={2}
              >
                <TutorialCard
                  data={tutorial}
                  isRemoving={isRemoving}
                  onRemove={handleRemove}
                  onClick={handleClick}
                />
              </Box>
            ))}
          </Flex>
        </Box>
      ) : null}
    </Page>
  )
}
